<template>
    <div class="telnyx-balance">
        <div class="header">
            <h3>Hesap Bakiyesi</h3>
            <a-button @click="loadBalance" :loading="loading" type="primary">
                <span class="material-icons">refresh</span>
                Yenile
            </a-button>
        </div>

        <div v-if="loading && !balance" class="loading-container">
            <a-spin size="large" />
            <p>Bakiye bilgileri yükleniyor...</p>
        </div>

        <div v-else-if="error" class="error-container">
            <a-alert 
                :message="error" 
                type="error" 
                show-icon 
                :closable="false"
            />
            <a-button @click="loadBalance" type="primary" style="margin-top: 10px;">
                Tekrar Dene
            </a-button>
        </div>

        <div v-else-if="balance" class="balance-content">
            <a-row :gutter="[24, 24]">
                <a-col :span="8">
                    <div class="modern-balance-card main-balance">
                        <div class="card-header">
                            <div class="card-icon">
                                <span class="material-icons">account_balance_wallet</span>
                            </div>
                            <div class="card-title">Ana Bakiye</div>
                        </div>
                        <div class="card-content">
                            <div class="balance-amount">
                                <span class="currency">{{ balance.data.currency }}</span>
                                <span class="amount">{{ formatAmount(balance.data.balance) }}</span>
                            </div>
                            <div class="progress-section">
                                <div class="progress-bar">
                                    <div class="progress-fill" :style="{ width: mainBalancePercentage + '%' }"></div>
                                </div>
                                <p class="balance-description">Toplam hesap bakiyesi</p>
                            </div>
                        </div>
                        <div class="card-footer">
                            <a-tag :color="balanceStatus.color" class="status-tag">
                                {{ balanceStatus.text }}
                            </a-tag>
                        </div>
                    </div>
                </a-col>

                <a-col :span="8">
                    <div class="modern-balance-card pending-balance">
                        <div class="card-header">
                            <div class="card-icon">
                                <span class="material-icons">schedule</span>
                            </div>
                            <div class="card-title">Bekleyen Tutarlar</div>
                        </div>
                        <div class="card-content">
                            <div class="balance-amount">
                                <span class="currency">{{ balance.data.currency }}</span>
                                <span class="amount">{{ formatAmount(balance.data.pending) }}</span>
                            </div>
                            <div class="progress-section">
                                <div class="progress-bar">
                                    <div class="progress-fill pending" :style="{ width: pendingPercentage + '%' }"></div>
                                </div>
                                <p class="balance-description">İşleme alınan tutarlar</p>
                            </div>
                        </div>
                        <div class="card-footer">
                            <span class="info-text">{{ pendingPercentage.toFixed(1) }}% beklemede</span>
                        </div>
                    </div>
                </a-col>

                <a-col :span="8">
                    <div class="modern-balance-card frozen-balance">
                        <div class="card-header">
                            <div class="card-icon">
                                <span class="material-icons">ac_unit</span>
                            </div>
                            <div class="card-title">Donmuş Bakiye</div>
                        </div>
                        <div class="card-content">
                            <div class="balance-amount">
                                <span class="currency">{{ balance.data.currency }}</span>
                                <span class="amount">{{ formatAmount(balance.data.frozen) }}</span>
                            </div>
                            <div class="progress-section">
                                <div class="progress-bar">
                                    <div class="progress-fill frozen" :style="{ width: frozenPercentage + '%' }"></div>
                                </div>
                                <p class="balance-description">Kullanıma kapalı tutar</p>
                            </div>
                        </div>
                        <div class="card-footer">
                            <span class="percentage-text">{{ frozenPercentage.toFixed(1) }}% donduruldu</span>
                        </div>
                    </div>
                </a-col>
            </a-row>

            <a-row :gutter="[24, 24]" style="margin-top: 32px;">
                <a-col :span="24">
                    <div class="modern-details-card">
                        <div class="details-header">
                            <div class="details-icon">
                                <span class="material-icons">info</span>
                            </div>
                            <h3>Detay Bilgiler</h3>
                        </div>
                        <div class="details-grid">
                            <div class="detail-item">
                                <div class="detail-label">Hesap Tipi</div>
                                <div class="detail-value">{{ balance.data.record_type }}</div>
                            </div>
                            <div class="detail-item">
                                <div class="detail-label">Para Birimi</div>
                                <div class="detail-value currency-badge">{{ balance.data.currency }}</div>
                            </div>
                            <div class="detail-item">
                                <div class="detail-label">Kullanılabilir Kredi</div>
                                <div class="detail-value">{{ balance.data.currency }} {{ formatAmount(balance.data.available_credit) }}</div>
                            </div>
                            <div class="detail-item">
                                <div class="detail-label">Bakiye Durumu</div>
                                <div class="detail-value">
                                    <a-tag :color="balanceStatus.color" class="modern-status-tag">
                                        {{ balanceStatus.text }}
                                    </a-tag>
                                </div>
                            </div>
                            <div class="detail-item">
                                <div class="detail-label">Son Güncelleme</div>
                                <div class="detail-value timestamp">{{ lastUpdated }}</div>
                            </div>
                        </div>
                    </div>
                </a-col>
            </a-row>

            <div v-if="totalBalance < 50" class="warning-section">
                <a-alert
                    message="Düşük Bakiye Uyarısı"
                    :description="`Hesap bakiyeniz ${balance.data.currency} ${formatAmount(balance.data.balance)} seviyesinde. Lütfen bakiye yüklemesi yapınız.`"
                    type="warning"
                    show-icon
                    :closable="false"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { WEBPHONE_TELNYX_API } from "../../../../../api/webphone.telnyx";
import log2 from "../../../../../utils/log2";
import moment from "moment";

const log = log2("TelnyxBalance.vue");

export default Vue.extend({
    name: "TelnyxBalance",
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            balance: null,
            error: null,
            lastUpdated: null,
        };
    },
    computed: {
        channel() {
            return this.value;
        },
        totalBalance() {
            if (!this.balance || !this.balance.data) return 0;
            return parseFloat(this.balance.data.balance) || 0;
        },
        balanceStatus() {
            if (!this.balance || !this.balance.data) return { color: "gray", text: "Bilinmiyor" };
            
            const mainBalance = this.totalBalance;
            if (mainBalance < 10) {
                return { color: "red", text: "Kritik Seviye" };
            } else if (mainBalance < 50) {
                return { color: "orange", text: "Düşük Seviye" };
            } else if (mainBalance < 100) {
                return { color: "yellow", text: "Orta Seviye" };
            } else {
                return { color: "green", text: "İyi Seviye" };
            }
        },
        mainBalancePercentage() {
            // Ana bakiye için sabit %100 (referans noktası)
            return 100;
        },
        pendingPercentage() {
            if (!this.balance || !this.balance.data || this.totalBalance === 0) return 0;
            const pending = parseFloat(this.balance.data.pending) || 0;
            return (pending / this.totalBalance) * 100;
        },
        frozenPercentage() {
            if (!this.balance || !this.balance.data || this.totalBalance === 0) return 0;
            const frozen = parseFloat(this.balance.data.frozen) || 0;
            return (frozen / this.totalBalance) * 100;
        },
    },
    methods: {
        async loadBalance() {
            if (!this.channel || !this.channel._id) {
                this.error = "Kanal bilgisi bulunamadı";
                return;
            }

            this.loading = true;
            this.error = null;

            try {
                log("Loading balance for channel:", this.channel._id);
                const response = await WEBPHONE_TELNYX_API.balance.getDetail(this.channel._id);
                
                this.balance = response;
                this.lastUpdated = moment().format("DD.MM.YYYY HH:mm:ss");
                
                log("Balance loaded successfully:", response);
            } catch (error) {
                console.error("Error loading balance:", error);
                this.error = error.response?.data?.error || error.message || "Bakiye bilgileri yüklenirken hata oluştu";
                log("Balance loading error:", this.error);
            } finally {
                this.loading = false;
            }
        },
        formatAmount(amount) {
            if (amount === null || amount === undefined) return "0.00";
            return Number(amount).toFixed(2);
        },
    },
    mounted() {
        this.loadBalance();
    },
});
</script>

<style scoped lang="scss">
.telnyx-balance {
    padding: 24px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;

        h3 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
        }

        .ant-btn {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        text-align: center;

        p {
            margin-top: 16px;
            color: var(--color-text-secondary);
        }
    }

    .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px 20px;
    }

    .balance-content {
        .modern-balance-card {
            background: #fff;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(0, 0, 0, 0.06);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 4px;
                background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
                opacity: 0;
                transition: opacity 0.3s ease;
            }

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
                
                &::before {
                    opacity: 1;
                }
            }

            &.main-balance::before {
                background: linear-gradient(90deg, #4ade80 0%, #22c55e 100%);
            }

            &.pending-balance::before {
                background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
            }

            &.frozen-balance::before {
                background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
            }

            .card-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 20px;

                .card-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    color: white;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .card-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #374151;
                    margin: 0;
                }
            }

            &.main-balance .card-header .card-icon {
                background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
            }

            &.pending-balance .card-header .card-icon {
                background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            }

            &.frozen-balance .card-header .card-icon {
                background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            }

            .card-content {
                margin-bottom: 20px;

                .balance-amount {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    margin-bottom: 16px;

                    .currency {
                        font-size: 18px;
                        font-weight: 600;
                        color: #6b7280;
                        background: #f3f4f6;
                        padding: 4px 8px;
                        border-radius: 6px;
                        line-height: 1;
                    }

                    .amount {
                        font-size: 32px;
                        font-weight: 700;
                        color: #111827;
                        line-height: 1;
                    }
                }

                .progress-section {
                    .progress-bar {
                        width: 100%;
                        height: 8px;
                        background: #f3f4f6;
                        border-radius: 4px;
                        overflow: hidden;
                        margin-bottom: 8px;

                        .progress-fill {
                            height: 100%;
                            border-radius: 4px;
                            transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
                            background: linear-gradient(90deg, #4ade80 0%, #22c55e 100%);
                            position: relative;

                            &::after {
                                content: '';
                                position: absolute;
                                top: 0;
                                left: 0;
                                right: 0;
                                bottom: 0;
                                background: linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 100%);
                                animation: shimmer 2s infinite linear;
                            }

                            &.pending {
                                background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
                            }

                            &.frozen {
                                background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
                            }
                        }
                    }

                    .balance-description {
                        margin: 0;
                        color: #6b7280;
                        font-size: 14px;
                        font-weight: 500;
                    }
                }
            }

            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;

                .status-tag {
                    border: none;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 12px;
                }

                .info-text {
                    color: #6b7280;
                    font-size: 13px;
                    font-weight: 500;
                }

                .percentage-text {
                    color: #374151;
                    font-size: 13px;
                    font-weight: 600;
                    background: #f9fafb;
                    padding: 4px 8px;
                    border-radius: 6px;
                }
            }
        }

        .modern-details-card {
            background: #fff;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(0, 0, 0, 0.06);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

            &:hover {
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
            }

            .details-header {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 24px;
                padding-bottom: 16px;
                border-bottom: 1px solid #f3f4f6;

                .details-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    color: white;
                    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                }

                h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #111827;
                }
            }

            .details-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                gap: 20px;

                .detail-item {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;

                    .detail-label {
                        font-size: 13px;
                        font-weight: 600;
                        color: #6b7280;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .detail-value {
                        font-size: 15px;
                        font-weight: 600;
                        color: #111827;

                        &.currency-badge {
                            display: inline-block;
                            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                            color: white;
                            padding: 6px 12px;
                            border-radius: 8px;
                            font-size: 14px;
                            font-weight: 700;
                            text-align: center;
                            min-width: 60px;
                        }

                        &.timestamp {
                            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                            background: #f9fafb;
                            padding: 6px 10px;
                            border-radius: 6px;
                            color: #374151;
                            border: 1px solid #e5e7eb;
                        }

                        .modern-status-tag {
                            border: none;
                            font-weight: 600;
                            padding: 6px 12px;
                            border-radius: 8px;
                            font-size: 13px;
                        }
                    }
                }
            }
        }

        .warning-section {
            margin-top: 32px;
        }
    }

    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
}
</style> 